import * as IkeModel from '@ikegps/core-shared';
import { IkeUnit } from '@ikegps/core-shared';
import { NumberInputState } from '../../../components/input/number.input.state';
import { Input } from '../../../components/input/input';
import { T } from '../../../i18n';
import { m, type MithrilVNode } from '../../../mithril';
import { CurrentState } from '../../../state/page.state';
import { FieldInfo } from '../../field.info/field.info';
import { CollectionEditCtrl } from '../collection.view.ctrl';
import type { CollectionFieldView } from './field.view';

const MAX_DEGREES = 360;
const MIN_DEGREES = 0;
const DEGREE_PRECISION = 3;

/**
 * Input state for orientation fields.
 * Values are stored and displayed in degrees with 0-360 range validation.
 */
class OrientationInputState extends NumberInputState {
	constructor(label: string) {
		super(label);
		this.precision = DEGREE_PRECISION;
		this.isLabelShown = false;
	}

	override parseInputValue(val: string) {
		const degrees = super.parseInputValue(val);

		if (degrees == null) {
			if (this.hasError()) {
				this.setError(T.ORIENTATION_INVALID);
			}
			return null;
		}

		if (degrees < MIN_DEGREES || degrees > MAX_DEGREES) {
			this.setError(T.ORIENTATION_INVALID);
			return null;
		}

		this.setError(null);
		return degrees;
	}

	override onBlur(e: FocusEvent): void {
		super.onBlur(e);
		if (this._value != null && !this.hasError()) {
			this.inputValue = this.formatInputValue(this._value);
		}
	}
}

function getValue(field: IkeModel.CollectionModelFieldOrientation, inputState: OrientationInputState): number | null {
	const value = field.value;

	if (!field.hasValue()) {
		return null;
	}

	if (field.isManual()) {
		return IkeUnit.round(value, inputState.precision);
	}

	if (field.isBearing() || field.isBackBearing()) {
		const angle = IkeUnit.convertAngle(value, IkeUnit.DEGREE, IkeUnit.RADIAN);
		return IkeUnit.round(angle, inputState.precision);
	}

	return null;
}

export const OrientationFieldView: CollectionFieldView<IkeModel.CollectionModelFieldOrientation> = {
	viewInline(field: IkeModel.CollectionModelFieldOrientation): string {
		return field.displayValue;
	},

	hasDetail(field: IkeModel.CollectionModelFieldOrientation) {
		return !field.isManual() && field.getCaptures().length > 0;
	},

	viewDetail(field: IkeModel.CollectionModelFieldOrientation): MithrilVNode {
		if (!OrientationFieldView.hasDetail(field)) {
			return null;
		}

		const collection = field.collection;
		const captures = field.getCaptures();
		return FieldInfo.viewCaptures(collection, captures);
	},

	viewEdit(field: IkeModel.CollectionModelFieldOrientation, ctrl: CollectionEditCtrl): MithrilVNode | string {
		let inputState = ctrl.changeState.getState(field.id) as OrientationInputState;
		if (inputState == null) {
			inputState = new OrientationInputState(field.schema.displayName);
			inputState.onValue(() => {
				field.Mutator.manual(inputState.value);
			});
			inputState.setValue(getValue(field, inputState));
			ctrl.changeState.addState(field.id, inputState);
		}

		const onmouseover = () => {
			CurrentState.setHoverId(field.id);
		};
		const onmouseout = () => {
			CurrentState.setHoverId(null);
		};
		const className = Input.CSS.container;
		return m('div', { className, onmouseover, onmouseout }, [Input.view(inputState)]);
	},

	hasClear() {
		return false;
	},
};
